/**
 * Le monde, en deux niveaux : la liste des zones, puis le détail d'une zone.
 *
 * Une liste à plat de 52 familles, 23 ressources et 26 donjons était
 * illisible. On navigue maintenant comme dans le jeu : on entre dans une zone,
 * et on y trouve ses monstres, ses ressources et son donjon.
 */
import type { Ctx } from "../contexte.ts"
import { sousOnglets } from "../contexte.ts"
import { esc, nb } from "../fmt.ts"
import { icone, ic } from "../sprites.ts"
import { item, type Recolte } from "../../sim/contenu.ts"
import { verdictFamille, resistancesFamille, ELEMENTS } from "../../sim/combat.ts"
import { accessible, tempsRecolte, niveauMetier, prixVente } from "../../sim/recolte.ts"
import { possedeCles, pronostic, nomCle, BOSS_TAUX, descentesPossibles } from "../../sim/donjon.ts"
import type { Contenu } from "../../sim/contenu.ts"
import type { Donjon } from "../../sim/donjon.ts"
import { indexer, origine, libelleOrigine, destination, sourcesCompletes } from "../../sim/provenance.ts"
import { SERIE_MAX } from "../../sim/simulate.ts"


/** Terrain d'ambiance associé à une zone, d'après son nom. */
/**
 * Repère de difficulté, par le seul **écart de niveau**.
 *
 * Le pronostic exact — « tu tiens 12 s » — a été retiré : il enlevait tout à
 * découvrir. Mais partir à l'aveugle dans 178 familles n'est pas mieux. Un
 * point de couleur suffit : il dit l'ordre de grandeur sans annoncer l'issue,
 * et il se lit sur le seul niveau, une information qu'on a déjà sous les yeux.
 */
const SEUIL_ORANGE = 1.0
const SEUIL_ROUGE = 1.3

function pastille(niveauCible: number, niveauJoueur: number): string {
  const r = niveauCible / Math.max(1, niveauJoueur)
  const [teinte, quoi] = r <= SEUIL_ORANGE ? ["vert", "à ta portée"]
    : r <= SEUIL_ROUGE ? ["orange", "au-dessus de toi, ça va piquer"]
    : ["rouge", "très au-dessus de ton niveau"]
  return `<span class="pastilleNiv ${teinte}" title="${quoi}" aria-label="${quoi}"></span>`
}

export function terrainDe(nomZone: string): string {
  const n = nomZone.toLowerCase()
  if (/nowel|frigost|glace|givre/.test(n)) return "cavene"
  if (/volcan|vulkania|brûlé|brulé/.test(n)) return "volcan"
  if (/otomaï|marais|marécage/.test(n)) return "marais"
  if (/abraknyde|forêt|foret|bois|futaie/.test(n)) return "foret"
  if (/brâkmar|bonta|astrub|cité|ville|amakna village/.test(n)) return "cite"
  if (/sufokia|moon|pwâk|valonia|corail/.test(n)) return "plage"
  if (/dimension|gelaxième|faille|crypte|souterrain/.test(n)) return "donjon"
  if (/wabbit|cania|plaine/.test(n)) return "champ"
  if (/île|archipel|baie|plage/.test(n)) return "plage"
  return "plaine"
}

/** Décor associé à un métier : on doit voir d'un coup d'œil ce qu'on récolte. */
export function terrainMetier(metier: number): string {
  return ({ 2: "foret", 24: "mine", 26: "jardin", 28: "champ", 36: "riviere", 41: "plaine" } as Record<number, string>)[metier] ?? "plaine"
}

export function vueMonde(c: Ctx): string {
  // Une descente qui vient d'avoir lieu passe devant tout le reste : c'est un
  // événement, il doit se lire avant de rendre la main.
  if (c.etat.derniereDescente) return vueDescente(c)
  const z = c.sous.startsWith("z") ? Number(c.sous.slice(1)) : null
  if (z !== null && c.contenu.zones[String(z)]) return detailZone(c, z)
  const actif = ["recolte", "donjons"].includes(c.sous) ? c.sous : "liste"
  return sousOnglets(actif, SOUS_MONDE) + (actif === "recolte" ? volerRecolte(c)
    : actif === "donjons" ? tousLesDonjons(c)
    : listeZones(c))
}

const SOUS_MONDE: [string, string][] = [["liste", "Zones"], ["recolte", "Récolte"], ["donjons", "Donjons"]]

/**
 * Tous les donjons, classés par niveau.
 *
 * Ils n'étaient atteignables qu'en ouvrant la zone qui les héberge, puis son
 * sous-onglet : deux gestes et une devinette — quelle zone abrite quel donjon ?
 * Or on choisit un donjon par son **niveau** et par la clé qu'on a en poche,
 * pas par sa région.
 */
function tousLesDonjons(c: Ctx): string {
  const { contenu, etat, p } = c
  if (!p) return ""
  const tous = Object.values(contenu.donjons)
    .sort((a, b) => (a.niveauMin || a.niveauOptimal) - (b.niveauMin || b.niveauOptimal))
  // Cent vingt-six donjons dont on n'a la clé que de quelques-uns : la liste
  // complète sert à savoir ce qui existe, le filtre à savoir ce qu'on peut
  // faire tout de suite.
  const enMain = tous.filter((d) => possedeCles(d, etat.inventaire))
  const djs = c.filtreDonjon === "cle" ? enMain
    : c.filtreDonjon === "reste" ? tous.filter((d) => !d.boss.some((b) => etat.bossVaincus.includes(b)))
    : tous

  return `<section class="carte">
    <h2>Donjons <em>${djs.length}${djs.length !== tous.length ? ` sur ${tous.length}` : ""}</em></h2>
    <p class="aide">Chaque donjon demande sa clé, consommée à l'entrée et perdue même en cas d'échec. Une descente se joue en direct : les salles s'enchaînent, les gardes tombent un par un, puis vient le boss.</p>
    <nav class="filtres">
      <button data-filtredonjon="" aria-current="${!c.filtreDonjon}">Tous</button>
      <button data-filtredonjon="cle" aria-current="${c.filtreDonjon === "cle"}">Clé en main <em>${enMain.length}</em></button>
      <button data-filtredonjon="reste" aria-current="${c.filtreDonjon === "reste"}">Jamais vaincus</button>
    </nav>
    ${djs.length === 0 ? `<p class="aide">Aucun donjon ici. Fabrique ou farme une clé, et il apparaîtra.</p>` : ""}
    <div class="liste">${djs.map((d) => {
      const cles = possedeCles(d, etat.inventaire)
      const zone = d.zone != null ? contenu.zones[String(d.zone)]?.nom : null
      const niv = d.niveauMin || d.niveauOptimal
      const battu = d.boss.some((b) => etat.bossVaincus.includes(b))
      // **Sans clé, la rangée n'est pas un bouton.** Un bouton désactivé avale
      // les clics de ses enfants : le lien vers la recette de la clé était donc
      // mort exactement dans le cas où l'on en a besoin.
      const ouvre = cles ? `<button class="rangee donjon" data-donjon="${d.id}">` : `<div class="rangee donjon verrou">`
      const butin = butinDonjon(contenu, d.boss)
      const ouvertB = c.butinOuvert === `d:${d.id}`
      return `<div class="rangeeBloc ${ouvertB ? "ouverte" : ""}">${ouvre}
        <span class="vignette" style="${icone("monstres", d.boss[0] ?? 0, 40)}"></span>
        <span class="nom">${pastille(niv, p.niveau)}${esc(d.nom)}${
          battu ? `<span class="fait" title="Boss déjà vaincu">🏆 vaincu</span>` : ""}</span>
        <span class="niv">niv ${niv} · ${d.salles} salles${zone ? ` · ${esc(zone)}` : ""}</span>
        <span class="risque ${cles ? "ok" : "ko"}">${cles ? "clé en main" : "sans clé"}</span>
        ${lienCle(c, d)}
        ${butin.length > 0
          ? `<span class="lien butinLien" data-butin="d:${d.id}"
               title="${ouvertB ? "Masquer" : "Voir"} ce que le boss lâche"
               >${butin.length} butin${butin.length > 1 ? "s" : ""} ${ouvertB ? "▴" : "▾"}</span>`
          : ""}
      ${cles ? "</button>" : "</div>"}
      ${serie(c, d)}
      ${ouvertB ? bandeButin(c, butin) : ""}
    </div>`
    }).join("")}</div>
  </section>`
}


/**
 * Où aller chercher la clé d'un donjon.
 *
 * Le lien menait toujours à l'atelier, « Aller fabriquer ». C'est vrai des cent
 * huit clés qui ont une recette — mais pas de la Petite Larve Dorée, qui ne se
 * fabrique pas : elle tombe des Larves. Balbouz s'est retrouvé devant un donjon
 * verrouillé dont le seul lien l'envoyait chercher dans un atelier où rien ne
 * l'attendait, et a redemandé si le Donjon des Larves était faisable — après
 * qu'on l'eut justement rendu faisable. Une porte fermée doit dire où est sa
 * clé, pas où elle n'est pas.
 */
function lienCle(c: Ctx, d: Donjon): string {
  const { contenu } = c
  const cle = d.cles[0]
  if (!cle) return `<span class="cle">${esc(nomCle(d, contenu))}</span>`
  const nom = nomCle(d, contenu)
  const icone16 = `<i class="dic" style="${icone("items", cle.item, 16)}"></i>`

  const recette = contenu.recettes.some((r) => r.resultat === cle.item)
  if (recette) {
    return `<span class="cle lien" data-aller="atelier:${esc(item(contenu, cle.item)?.nom ?? "")}"
        title="Aller fabriquer ${esc(nom)}">${icone16}${esc(nom)} ›</span>`
  }

  // Pas de recette : la clé se farme. On envoie sur la famille qui la lâche le
  // plus souvent — un clic, et le personnage s'y met.
  const src = sourcesCompletes(contenu, cle.item)
  const f = [...src.familles].sort((a, b) => b.taux - a.taux)[0]
  if (f) {
    return `<span class="cle lien" data-famille="${f.id}"
        title="Se battre contre ${esc(f.nom)} : ${f.taux.toFixed(1)} % de chance par victoire"
        >${icone16}${esc(nom)} · ${esc(f.nom)} ›</span>`
  }
  return `<span class="cle">${esc(nom)}</span>`
}

/**
 * Toutes les ressources récoltables, d'un seul écran.
 *
 * Elles n'étaient atteignables qu'en ouvrant une zone puis son sous-onglet
 * « Ressources » : trois gestes pour une action qu'on répète, et il fallait
 * deviner quelle zone héberge quoi. Les ressources ne sont d'ailleurs pas
 * rattachées à une zone dans les données — elles dépendent du métier.
 */
function volerRecolte(c: Ctx): string {
  const { contenu, p } = c
  if (!p) return ""
  const parMetier = new Map<number, { r: Recolte; it: NonNullable<ReturnType<typeof item>> }[]>()
  for (const r of Object.values(contenu.recoltes)) {
    const it = item(contenu, r.item)
    if (!it) continue
    parMetier.set(r.metier, [...(parMetier.get(r.metier) ?? []), { r, it }])
  }
  const metiers = [...parMetier].sort((a, b) => a[0] - b[0])

  // **Ce qui accélère la récolte, dit une fois pour toutes.**
  //
  // La question est revenue trois fois dans le chat en deux jours — « c'est quoi
  // les stats utiles pour la récolte ? que les PM ? » — et les joueurs se
  // répondaient entre eux « les PM, j'ai l'impression ». Ils avaient un quart de
  // la réponse : le niveau de métier pèse davantage, et la classe ne pèse rien.
  // Une règle que personne ne peut lire se devine mal.
  const s = c.s
  const entete = `<section class="carte">
    <h2>Vitesse de récolte</h2>
    <div class="bilan">
      <div class="chiffre"><span>Cadence</span><b>×${(s?.recolte ?? 1).toFixed(2)}</b></div>
      <div class="chiffre"><span>PM</span><b>${nb(s?.pm ?? 3)}</b></div>
    </div>
    <p class="aide">Trois choses accélèrent une récolte, et une seule est visible ici.
      <b>Le niveau de métier</b> d'abord : il divise le temps de ramassage, jusqu'à près de
      deux fois et demie à 200. <b>Les PM</b> de ton équipement ensuite, qui multiplient
      la cadence — c'est le <b>×${(s?.recolte ?? 1).toFixed(2)}</b> ci-dessus. Enfin les
      <b>Dofus</b>, dont l'effet vaut pour tout le compte.
      <b>La classe n'y change rien</b> : tout le monde part à ${nb(3)} PM.</p>
  </section>`

  return entete + metiers.map(([mid, liste]) => {
    const mj = contenu.metiers[String(mid)]
    const niv = niveauMetier(p, mid)
    const ouvertes = liste.filter(({ r }) => accessible(p, r)).length
    // Six métiers dépliés font une page de 85 lignes qu'on parcourt au pouce.
    // On n'ouvre que celui qu'on vient chercher ; le métier en cours s'ouvre
    // tout seul, puisque c'est celui qu'on regarde.
    const enCours = p.activite?.type === "recolte"
      && contenu.recoltes[String(p.activite.ressourceId)]?.metier === mid
    return `<section class="carte">
      <details class="metierPli" ${enCours ? "open" : ""}>
      <summary><h2>${ic(`metier-${mj?.icone ?? 0}`, 22)}${esc(mj?.nom ?? "Métier")} <em>niveau ${niv} · ${ouvertes} / ${liste.length}</em></h2></summary>
      <div class="liste">${liste
        .sort((a, b) => a.r.niveauRequis - b.r.niveauRequis)
        .map(({ r, it }) => {
          const dispo = accessible(p, r)
          const actif = p.activite?.type === "recolte" && p.activite.ressourceId === r.item
          return `<button class="rangee ${actif ? "actif" : ""} ${dispo ? "" : "verrou"}" data-recolte="${r.item}" ${dispo ? "" : "disabled"}>
            <span class="vignette" style="${icone("items", it.id, 40)}"></span>
            <span class="nom">${esc(it.nom)}</span>
            <span class="niv">niveau de métier ${r.niveauRequis}</span>
            <span class="vitesse">${dispo ? `${tempsRecolte(r, niv).toFixed(1)} s · ${nb(prixVente(it))} k` : `🔒 ${niv}/${r.niveauRequis}`}</span>
          </button>`
        }).join("")}</div>
      </details>
    </section>`
  }).join("")
}

/**
 * Ce qu'on lit **après** la descente.
 *
 * La descente ayant été jouée sous les yeux du joueur, cet écran n'a plus à la
 * raconter salle par salle : il annonce l'issue et remet le butin.
 */
function vueDescente(c: Ctx): string {
  const d = c.etat.derniereDescente!
  const dj = c.contenu.donjons[String(d.donjon)]
  const butin = Object.entries(d.butin)
  // Le taux de chute, montré noir sur blanc. « Quatre donjons sans galet » ne
  // se comprend qu'en sachant qu'il tombe à 6 % : sans le chiffre, le joueur
  // ne sait pas s'il est malchanceux ou si le jeu est cassé.
  const table = (dj?.boss ?? []).flatMap((bid) => c.contenu.monstres[String(bid)]?.butin ?? [])
    .map((b) => ({
      id: b.item,
      nom: item(c.contenu, b.item)?.nom ?? "?",
      chance: Math.min(100, Math.max(...b.taux) * BOSS_TAUX),
      obtenu: d.butin[b.item] ?? 0,
    }))
    .filter((t, i, all) => all.findIndex((x) => x.id === t.id) === i)
    .sort((a, b) => b.chance - a.chance || a.nom.localeCompare(b.nom))
  const minutes = Math.floor(d.dureeS / 60)
  const duree = minutes > 0 ? `${minutes} min ${Math.round(d.dureeS % 60)} s` : `${Math.round(d.dureeS)} s`

  // Une série résume plusieurs descentes : les chiffres sont cumulés, et
  // « salle 5 sur 5 » ne veut plus rien dire — on annonce les descentes gagnées.
  const runs = d.runs ?? 1
  const gagnees = d.gagnees ?? (d.reussi ? 1 : 0)
  return `
  <section class="carte">
    <h2>${esc(dj?.nom ?? "Donjon")} <em>${runs > 1
      ? `${nb(gagnees)} / ${nb(runs)} descentes`
      : d.reussi ? "vaincu" : "échec"}</em></h2>
    <p class="verdict ${d.reussi ? "ok" : "ko"}">${runs > 1
      ? (d.reussi
          ? `Série terminée : ${nb(gagnees)} descente${gagnees > 1 ? "s" : ""} gagnée${gagnees > 1 ? "s" : ""}.`
          : `La série s'est arrêtée salle ${d.salle} sur ${d.salles}, après ${nb(gagnees)} descente${
              gagnees > 1 ? "s" : ""} gagnée${gagnees > 1 ? "s" : ""}. Les clés restantes ne sont pas consommées.`)
      : d.reussi
        ? `Le boss est tombé.`
        : `Ton personnage est tombé salle ${d.salle} sur ${d.salles}. La clé est perdue.`}</p>

    <div class="bilan">
      <div class="chiffre"><span>${runs > 1 ? "Descentes" : "Salles"}</span><b>${runs > 1
        ? `${nb(gagnees)} <em>/ ${nb(runs)}</em>`
        : `${d.reussi ? d.salles : d.salle} <em>/ ${d.salles}</em>`}</b></div>
      <div class="chiffre"><span>Adversaires</span><b>${nb(d.vaincus)}</b></div>
      <div class="chiffre"><span>Durée</span><b>${esc(duree)}</b></div>
      <div class="chiffre"><span>Expérience</span><b>${nb(d.xp)}</b></div>
      <div class="chiffre"><span>Vivres</span><b>${nb(d.vivres)}</b></div>
      <div class="chiffre"><span>Butin</span><b>${nb(butin.reduce((a, [, n]) => a + n, 0))}</b></div>
    </div>

    ${d.niveaux > 0 ? `<p class="verdict ok">${d.niveaux === 1
      ? "Un niveau gagné en chemin." : `${d.niveaux} niveaux gagnés en chemin.`}</p>` : ""}
    ${d.premiere ? `<p class="verdict ok">Premier passage : le boss rejoint ta collection.</p>` : ""}

    ${d.reussi ? `<h2>Butin du boss <em>${nb(table.length)} objets</em></h2>
      <p class="aide">Un boss lâche trois fois le taux affiché en jeu. Ce qui n'est pas tombé cette fois reste à ce taux la prochaine — quatre descentes sans voir un objet à 6 % n'a rien d'anormal.</p>
      <div class="liste">${table.map((t) => `
        <div class="piece ${t.obtenu ? "tombe" : "rate"}">
          <span class="vignette" style="${icone("items", t.id, 40)}"></span>
          <div class="txt">
            <span class="nom">${esc(t.nom)}</span>
            <span class="niv">${t.chance.toFixed(1)} % par victoire</span>
          </div>
          <span class="risque ${t.obtenu ? "ok" : ""}">${t.obtenu ? `×${t.obtenu}` : "—"}</span>
        </div>`).join("")}</div>`
      : `<p class="aide">Rien n'est remonté : le butin d'une descente n'est remis qu'à la sortie.</p>`}

    <button class="primaire" id="fermerDescente">Fermer</button>
  </section>`
}

function listeZones(c: Ctx): string {
  const { contenu, p, s } = c
  if (!p || !s) return ""
  const q = c.recherche.trim().toLowerCase()
  const zones = Object.values(contenu.zones).sort((a, b) => a.niveauMin - b.niveauMin)

  const champ = `<input class="recherche" id="rechercheMonde" type="search"
    placeholder="Chercher un monstre, une ressource, un objet…"
    value="${esc(c.recherche)}" autocomplete="off" />`

  if (q) return `<section class="carte">
    <h2>Recherche</h2>
    ${champ}
    <div id="resultatsMonde">${resultats(c, q)}</div>
  </section>`

  const carte = (z: (typeof zones)[number]) => {
    const fams = Object.values(contenu.familles).filter((f) => f.zone === z.id)
    const djs = Object.values(contenu.donjons).filter((d) => d.zone === z.id)
    if (!fams.length && !djs.length) return ""
    return `
    <button class="tuileZone" data-sous="z${z.id}"
            style="background-image:url(/img/terrain/${terrainDe(z.nom)}.webp)">
      <span class="voileZone"></span>
      <span class="titre">${pastille(z.niveauMin, p?.niveau ?? 1)}${esc(z.nom)}</span>
      <span class="meta">niv ${z.niveauMin}–${z.niveauMax} · ${fams.length} famille${fams.length > 1 ? "s" : ""}${djs.length ? ` · ${djs.length} donjon${djs.length > 1 ? "s" : ""}` : ""}</span>
    </button>`
  }

  const orphelines = Object.values(contenu.familles).filter((f) => f.zone == null)
  return `
  <section class="carte">
    <h2>Le monde <em>${zones.length} zones</em></h2>
    ${champ}
    <p class="aide">Tout est accessible dès le départ. Ce qui te barre la route, c'est la puissance des monstres : chaque famille te dit si tu tiens.</p>
    <div class="grilleZones">${zones.map(carte).join("")}</div>
  </section>
  ${orphelines.length ? `<section class="carte">
    <h2>Ailleurs</h2>
    <div class="liste">${orphelines.map((f) => rangeeFamille(c, f)).join("")}</div>
  </section>` : ""}`
}

/**
 * Résultats de recherche : familles, ressources, **et objets**.
 *
 * Chercher un objet est le cas le plus utile : on lit « il me faut une Plume de
 * Piou » dans l'atelier et on veut savoir où aller. La recherche remonte donc
 * jusqu'aux familles qui le lâchent.
 */
export function resultats(c: Ctx, q: string): string {
  const { contenu, p, s } = c
  if (!p || !s) return ""
  const idx = indexer(contenu)
  const nomZone = (id: number | null | undefined) =>
    id != null ? contenu.zones[String(id)]?.nom ?? "" : "Ailleurs"

  const familles = Object.values(contenu.familles)
    .filter((f) => f.nom.toLowerCase().includes(q))
    .slice(0, 12)

  const ressources = Object.values(contenu.recoltes)
    .map((r) => ({ r, it: item(contenu, r.item) }))
    .filter((x) => x.it && x.it.nom.toLowerCase().includes(q))
    .slice(0, 12)

  // Objets : on remonte à leur source, **quelle qu'elle soit**.
  //
  // Seuls les butins de famille étaient retenus : chercher « Galet cramoisi »
  // ne donnait rien, alors qu'il tombe de cent seize boss de donjon. Tout ce
  // qui venait d'un donjon, d'une récolte, d'un craft ou d'une collection était
  // écarté avant même l'affichage.
  const objets = Object.values(contenu.items)
    .filter((it) => it.nom.toLowerCase().includes(q))
    .map((it) => ({ it, o: origine(idx, it.id) }))
    .filter((x) => x.o.type !== "inconnue")
    .slice(0, 12)

  const rien = !familles.length && !ressources.length && !objets.length
  if (rien) return `<p class="aide">Rien trouvé pour « ${esc(q)} ».</p>`

  const bloc = (titre: string, corps: string) =>
    corps ? `<h2>${titre}</h2><div class="liste">${corps}</div>` : ""

  return `
  ${bloc("Objets", objets.map(({ it, o }) => {
    // Chaque source a sa manière d'y aller : une famille se farme d'un clic,
    // un donjon renvoie à la liste des donjons, un craft à l'atelier.
    const ou = destination(o, it.nom)
    const actions = o.type === "drop"
      ? o.familles.map((fa) => `
          <button class="mini primaire" data-famille="${fa.id}">${esc(fa.nom)} · ${fa.taux.toFixed(1)} %</button>`).join("")
      : o.type === "donjon"
        // Chaque bouton mène à **sa zone**, sur l'onglet des donjons : la liste
        // générale des cent vingt-six ne disait pas lequel ouvrir. Et « et 115
        // autres » sans moyen de les voir n'aidait personne : la liste se déplie.
        ? (() => {
            const ouvert = c.sourcesOuvertes === it.id
            const liste = ouvert ? sourcesCompletes(contenu, it.id).donjons : o.donjons
            const bouton = (d: { id: number; nom: string; niveau: number; taux: number }) => {
              const z = contenu.donjons[String(d.id)]?.zone
              return `<button class="mini primaire" data-aller="${z != null ? `zone:${z}:dj` : "donjons"}"
                >${esc(d.nom)} · niv ${d.niveau} <em>${d.taux.toFixed(1)} %</em></button>`
            }
            return liste.map(bouton).join("") + (o.total > o.donjons.length
              ? `<button class="mini" data-sources="${it.id}">${
                  ouvert ? "réduire" : `voir les ${o.total}`}</button>`
              : "")
          })()
        : ou
          ? `<button class="mini primaire" data-aller="${esc(ou)}">${esc(libelleOrigine(o))} ›</button>`
          : ""
    const deplie = o.type === "donjon" && c.sourcesOuvertes === it.id
    return `
    <div class="piece">
      <span class="vignette" style="${icone("items", it.id, 40)}"></span>
      <div class="txt">
        <span class="nom">${esc(it.nom)}</span>
        <span class="niv">niv ${it.niveau} · ${esc(libelleOrigine(o))}${
          o.type === "donjon" && o.donjons[0] ? ` · <b>${o.donjons[0].taux.toFixed(1)} %</b> par victoire` : ""}</span>
      </div>
      <div class="boutons ${deplie ? "toutes" : ""}">${actions}</div>
    </div>`
  }).join(""))}

  ${bloc("Familles", familles.map((f) => rangeeFamille(c, f, nomZone(f.zone))).join(""))}

  ${bloc("Ressources", ressources.map(({ r, it }) => {
    const dispo = accessible(p, r)
    const niv = niveauMetier(p, r.metier)
    return `<button class="rangee ${dispo ? "" : "verrou"}" data-recolte="${r.item}" ${dispo ? "" : "disabled"}>
      <span class="vignette" style="${icone("items", it!.id, 40)}"></span>
      <span class="nom">${esc(it!.nom)}</span>
      <span class="niv">${ic(`metier-${contenu.metiers[String(r.metier)]?.icone ?? 0}`, 16)} ${esc(contenu.metiers[String(r.metier)]?.nom ?? "")} · niveau ${r.niveauRequis}</span>
      <span class="risque ${dispo ? "ok" : "ko"}">${dispo ? "accessible" : `🔒 ${niv}/${r.niveauRequis}`}</span>
      <span class="vitesse">${tempsRecolte(r, niv).toFixed(1)} s · ${nb(prixVente(it!))} k</span>
    </button>`
  }).join(""))}`
}

function rangeeFamille(c: Ctx, f: { id: number; nom: string; monstres: number[]; niveauMin: number; niveauMax: number }, zone = ""): string {
  const { p, s, contenu } = c
  if (!p || !s) return ""
  const v = verdictFamille(s, contenu, f.id, p.pv, p.niveau)
  if (!v) return ""
  const actif = p.activite?.type === "combat" && p.activite.familleId === f.id
  const butin = butinFamille(contenu, f.monstres)
  const ouvert = c.butinOuvert === `f:${f.id}`
  return `
  <div class="rangeeBloc ${ouvert ? "ouverte" : ""}">
    <button class="rangee ${actif ? "actif" : ""}" data-famille="${f.id}">
      <span class="vignette" style="${icone("monstres", f.monstres[0] ?? 0, 40)}"></span>
      <span class="nom">${pastille(f.niveauMax, p.niveau)}${esc(f.nom)}${marqueSucces(c, f.id)}</span>
      <span class="niv">niv ${f.niveauMin}${f.niveauMax !== f.niveauMin ? `–${f.niveauMax}` : ""} · ${f.monstres.length} monstre${f.monstres.length > 1 ? "s" : ""}${zone ? ` · ${esc(zone)}` : ""}</span>
      <span class="vitesse">${v.tempsParKillS < 60
        ? `<b>${nb(v.xpParVictoire)} XP</b> par victoire`
        : "trop lent"}</span>
      ${bandeResistances(c, f.id)}
      ${butin.length > 0
        // Un élément cliquable **dans** le bouton, jamais un bouton imbriqué :
        // c'est invalide, et le gestionnaire de clics résout de toute façon le
        // plus proche ancêtre correspondant.
        ? `<span class="lien butinLien" data-butin="f:${f.id}"
             title="${ouvert ? "Masquer" : "Voir"} ce que cette famille lâche"
             >${butin.length} butin${butin.length > 1 ? "s" : ""} ${ouvert ? "▴" : "▾"}</span>`
        : ""}
    </button>
    ${ouvert ? bandeButin(c, butin) : ""}
  </div>`
}

/**
 * Ce qu'une famille lâche, tous ses monstres confondus.
 *
 * On retient le **meilleur taux** de chaque objet : dans une famille, les
 * monstres se ressemblent mais pas leurs tables, et c'est le meilleur qui dit
 * ce qu'on peut espérer en y allant.
 */
export function butinFamille(
  c: Contenu, monstres: number[],
): { item: number; nom: string; taux: number; niveau: number }[] {
  const par = new Map<number, { item: number; nom: string; taux: number; niveau: number }>()
  for (const mid of monstres) {
    const m = c.monstres[String(mid)]
    if (!m) continue
    for (const b of m.butin) {
      const it = item(c, b.item)
      if (!it) continue
      const taux = Math.max(...b.taux)
      const vu = par.get(b.item)
      if (!vu) par.set(b.item, { item: b.item, nom: it.nom, taux, niveau: it.niveau })
      else vu.taux = Math.max(vu.taux, taux)
    }
  }
  return [...par.values()].sort((a, b) => b.taux - a.taux || a.niveau - b.niveau)
}

/** Le butin d'un donjon : les tables de ses boss, au taux qu'ils appliquent. */
export function butinDonjon(
  c: Contenu, boss: number[],
): { item: number; nom: string; taux: number; niveau: number }[] {
  const par = new Map<number, { item: number; nom: string; taux: number; niveau: number }>()
  for (const bid of boss) {
    const m = c.monstres[String(bid)]
    if (!m) continue
    for (const b of m.butin) {
      const it = item(c, b.item)
      if (!it) continue
      // Un boss triple le taux affiché en jeu (GDD §20) : on montre ce qu'on
      // obtient vraiment, pas la valeur théorique de la table.
      const taux = Math.min(100, Math.max(...b.taux) * BOSS_TAUX)
      const vu = par.get(b.item)
      if (!vu) par.set(b.item, { item: b.item, nom: it.nom, taux, niveau: it.niveau })
      else vu.taux = Math.max(vu.taux, taux)
    }
  }
  return [...par.values()].sort((a, b) => b.taux - a.taux || a.niveau - b.niveau)
}

/**
 * Le butin, en bande horizontale.
 *
 * Une table de butin fait souvent vingt entrées, parfois cent : en colonne elle
 * repousserait toute la liste des familles hors de l'écran. En bande, elle tient
 * sur une ligne et se fait défiler du doigt, comme un rayon.
 *
 * Chaque vignette mène à sa fiche : c'est la même question que « où trouver
 * ça ? », prise par l'autre bout.
 */
function bandeButin(c: Ctx, butin: { item: number; nom: string; taux: number; niveau: number }[]): string {
  const { etat } = c
  // Le nom sous la bande, et non en infobulle flottante : la bande défile
  // horizontalement, donc elle rogne verticalement — une bulle posée au-dessus
  // d'une vignette serait coupée. L'infobulle native reste là pour le clavier
  // et les lecteurs d'écran, mais elle met une seconde à venir et personne ne
  // survole une table de vingt objets à une seconde par objet.
  return `<div class="bandeButinBloc">
    <div class="bandeButin" role="list">
      ${butin.map((b) => {
        const enSac = etat.inventaire[b.item] ?? 0
        const detail = `${b.nom} · ${b.taux.toFixed(1)} %${enSac > 0 ? ` · ${nb(enSac)} en réserve` : ""}`
        return `<button class="butin ${enSac > 0 ? "possede" : ""}" role="listitem"
          data-aller="monde:${esc(b.nom)}" data-nom="${esc(detail)}" title="${esc(detail)}">
          <span class="ic" style="${icone("items", b.item, 32)}"></span>
          <span class="tx">${b.taux.toFixed(b.taux < 1 ? 1 : 0)} %</span>
          ${enSac > 0 ? `<span class="qte">${nb(enSac)}</span>` : ""}
        </button>`
      }).join("")}
    </div>
    <p class="butinNom" aria-live="polite">${esc(butin[0]?.nom ?? "")}${
      butin[0] ? ` · ${butin[0].taux.toFixed(1)} %` : ""}</p>
  </div>`
}

/**
 * Les résistances de la famille, en quatre chiffres.
 *
 * C'est ce qui dit quelle caractéristique monter, et ça ne s'affichait nulle
 * part. Négatif = faiblesse, et donc l'élément à jouer : il est mis en avant.
 */
export function bandeResistances(c: Ctx, familleId: number): string {
  const r = resistancesFamille(c.contenu, familleId)
  if (!r) return ""
  const faible = ELEMENTS.reduce((a, e) => (r[e] < r[a] ? e : a), ELEMENTS[0])
  return `<span class="resis">${ELEMENTS.map((e) => `
    <span class="r ${r[e] < 0 ? "faible" : r[e] >= 25 ? "dure" : ""} ${e === faible ? "meilleur" : ""}"
      title="${r[e] < 0 ? "Faiblesse" : "Résistance"} ${e} : ${r[e]} %">
      ${ic(e, 14)}<b>${r[e] > 0 ? "+" : ""}${r[e]}</b>
    </span>`).join("")}</span>`
}

/**
 * La zone n'a plus d'onglet « Ressources ».
 *
 * Il listait **toutes** les ressources du jeu, les mêmes d'une zone à l'autre :
 * les récoltes ne sont pas rattachées à une zone dans les données, elles
 * dépendent du métier. C'était donc un doublon trompeur de l'onglet « Récolte »
 * du monde, qui fait la même chose en mieux.
 */
const SOUS_ZONE: [string, string][] = [["mob", "Monstres"], ["dj", "Donjons"]]

function detailZone(c: Ctx, zid: number): string {
  const { etat, contenu, p, s } = c
  if (!p || !s) return ""
  const z = contenu.zones[String(zid)]!
  // **Du plus faible au plus fort.** L'ordre du contenu n'a aucun sens pour le
  // joueur : Amakna, la zone de départ, ouvrait sur « Bandits d'Amakna, niveau
  // 35 » quand on arrive au niveau 1. On trie par niveau, ce que fait déjà la
  // liste des donjons.
  const fams = Object.values(contenu.familles).filter((f) => f.zone === zid)
    .sort((a, b) => a.niveauMin - b.niveauMin || a.niveauMax - b.niveauMax)
  const djs = Object.values(contenu.donjons).filter((d) => d.zone === zid)
  const onglet = c.sousZone

  const corps =
    onglet === "dj" ? (djs.length === 0 ? `<p class="aide">Aucun donjon dans cette zone.</p>` :
      `<div class="liste">${djs.map((d) => {
        const cles = possedeCles(d, etat.inventaire)
        const pro = pronostic(p, s, d, contenu)
        // Même règle qu'ailleurs : sans clé la rangée n'est pas un bouton, pour
        // que le lien vers la recette de la clé reste cliquable.
        const ouvre = cles
          ? `<button class="rangee donjon ${pro.reussi ? "" : "verrou"}" data-donjon="${d.id}">`
          : `<div class="rangee donjon verrou">`
        return `${ouvre}
          <span class="vignette" style="${icone("monstres", d.boss[0] ?? 0, 40)}"></span>
          <span class="nom">🏰 ${esc(d.nom)}</span>
          <span class="niv">${d.salles} salles</span>
          ${d.cles[0]
            ? `<span class="cle lien" data-aller="atelier:${esc(item(contenu, d.cles[0].item)?.nom ?? "")}"
                 title="Aller fabriquer ${esc(nomCle(d, contenu))}">
                <i class="dic" style="${icone("items", d.cles[0].item, 16)}"></i>${esc(nomCle(d, contenu))} ›
              </span>`
            : ""}
          <span class="risque ${cles ? "ok" : "ko"}">${cles ? "clé en main" : "sans clé"}</span>
        ${cles ? "</button>" : "</div>"}`
      }).join("")}</div>`)
    : `<div class="liste">${fams.map((f) => rangeeFamille(c, f)).join("")}</div>`

  return `
  <section class="carte banniereZone" style="background-image:url(/img/terrain/${terrainDe(z.nom)}.webp)">
    <span class="voileZone"></span>
    <button class="retour" data-sous="liste">‹ Toutes les zones</button>
    <h2 class="titreZone">${esc(z.nom)}</h2>
    <p class="metaZone">niveaux ${z.niveauMin} à ${z.niveauMax}</p>
  </section>
  ${sousOnglets(onglet, SOUS_ZONE.filter(([k]) => k !== "dj" || djs.length > 0), "souszone")}
  <section class="carte">${corps}</section>`
}

/**
 * Où en est le succès de cette famille, sur la rangée où on la choisit.
 *
 * « Ça évite les aller-retours avec la vue succès », demandait Balbouz, et c'est
 * exactement le point : on décide quoi farmer **ici**, pas dans l'écran des
 * succès. Cent victoires font le succès d'une famille ; tant qu'il n'est pas
 * pris on affiche le compte, une fois pris on le dit et on n'en parle plus.
 */
function marqueSucces(c: Ctx, familleId: number): string {
  const n = (c.etat.killsFamille ?? {})[familleId] ?? 0
  if (n >= SEUIL_SUCCES_FAMILLE) {
    return `<span class="fait" title="Succès de famille obtenu : ${SEUIL_SUCCES_FAMILLE} victoires">✔ succès</span>`
  }
  if (n === 0) return ""
  return `<span class="enCours" title="Succès de famille à ${SEUIL_SUCCES_FAMILLE} victoires">${n} / ${SEUIL_SUCCES_FAMILLE}</span>`
}

/** Cent victoires, comme le pose `succes.ts` pour chaque famille. */
const SEUIL_SUCCES_FAMILLE = 100

/**
 * Enchaîner plusieurs descentes d'un geste.
 *
 * « C'est un peu chiant de devoir cliquer sur recommencer » (Vyshu). On ne
 * propose la série que si l'on a de quoi la faire, et on annonce le nombre exact
 * qu'elle atteindra : promettre « toutes » et s'arrêter à cinquante serait le
 * même mensonge que le « MAX » du craft.
 */
function serie(c: Ctx, d: Donjon): string {
  const n = descentesPossibles(d, c.etat.inventaire)
  if (n < 2) return ""
  const combien = Math.min(n, SERIE_MAX)
  return `<div class="serieDonjon">
    <button class="mini" data-serie="${d.id}" data-combien="${combien}"
      title="Enchaîner ${combien} descentes tant que le personnage tient debout ; une défaite arrête la série">
      Enchaîner ×${nb(combien)}</button>
    <span class="aide">${nb(n)} clé${n > 1 ? "s" : ""} en réserve${
      n > SERIE_MAX ? ` — ${nb(SERIE_MAX)} descentes au maximum par série` : ""}</span>
  </div>`
}
